import { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import ResellerSignup from '@/pages/ResellerSignup';
import AutoLogin from '@/pages/AutoLogin';
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminProducts from '@/pages/admin/Products';
import AdminResellers from '@/pages/admin/Resellers';
import AdminRevenueTracking from '@/pages/admin/RevenueTracking';
import AdminOrders from '@/pages/admin/Orders';
import AdminWallets from '@/pages/admin/Wallets';
import AdminTransactions from '@/pages/admin/Transactions';
import AdminClients from '@/pages/admin/Clients';
import AdminActivityLogs from '@/pages/admin/ActivityLogs';
import AdminNotifications from '@/pages/admin/Notifications';
import AdminInvoices from '@/pages/admin/Invoices';
import AdminSupport from '@/pages/admin/Support';
import AdminUserManagement from '@/pages/admin/UserManagement';
import AdminQuotations from '@/pages/admin/Quotations';
import AdminCoupons from '@/pages/admin/Coupons';
import AdminDocuments from '@/pages/admin/Documents';
import AdminOffers from '@/pages/admin/Offers';
import AdminDomainManager from '@/pages/admin/DomainManager';
import AdminWhatsAppSettings from '@/pages/admin/WhatsAppSettings';
import ResellerDashboard from '@/pages/reseller/Dashboard';
import ResellerPricing from '@/pages/reseller/Pricing';
import ResellerCatalog from '@/pages/reseller/Catalog';
import SaaSOrders from '@/pages/admin/SaaSOrders';
import ResellerDomains from '@/pages/reseller/Domains';
import ResellerClients from '@/pages/reseller/Clients';
import ResellerWhiteLabel from '@/pages/reseller/WhiteLabel';
import MyServices from '@/pages/reseller/MyServices';
import ResellerQuotations from '@/pages/reseller/Quotations';
import ResellerOrders from '@/pages/reseller/Orders';
import ResellerWallet from '@/pages/reseller/Wallet';
import ResellerWalletTransactions from '@/pages/reseller/WalletTransactions';
import ResellerInvoices from '@/pages/reseller/Invoices';
import ResellerSupport from '@/pages/reseller/Support';
import PartnerDocuments from '@/pages/partner/Documents';
import ClientDashboard from '@/pages/client/Dashboard';
import ClientServices from '@/pages/client/Services';
import ClientBilling from '@/pages/client/Billing';
import ClientSupport from '@/pages/client/Support';
import ClientReferralProgram from '@/pages/client/ReferralProgram';
import ClientOurProducts from '@/pages/client/OurProducts';
import ClientQuotesAndOrders from '@/pages/client/QuotesAndOrders';
import ClientBuyDomain from '@/pages/client/BuyDomain';
import ClientSignup from '@/pages/ClientSignup';
import SecuritySettings from '@/pages/SecuritySettings';
import PublicLanding from '@/pages/PublicLanding';
import Profile from '@/pages/Profile';
import KYCPortal from '@/pages/partner/KYCPortal';
import AccountEditPage from '@/pages/partner/AccountEditPage';
import ProfileEditPage from '@/pages/partner/ProfileEditPage';
import PartnerDashboardProfile from '@/pages/partner/PartnerDashboardProfile';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { BrandingProvider } from '@/context/BrandingContext';
import { AlertTriangle, ShieldOff, HeadphonesIcon, ArrowRight } from 'lucide-react';

/* ── Suspended Account Screen ───────────────────────────────────────── */
const SuspendedScreen = ({ reason, role }) => {
  const navigate = useNavigate();
  const supportPath = role === 'client' ? '/client/support' : '/reseller/support';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Status card */}
        <div className="bg-white rounded-2xl shadow-lg border border-red-100 overflow-hidden">
          {/* Red top band */}
          <div className="bg-gradient-to-r from-red-500 to-rose-500 px-6 py-5 text-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-white/20 rounded-xl">
                <ShieldOff className="h-5 w-5" />
              </div>
              <span className="text-sm font-semibold uppercase tracking-wider opacity-90">Account Suspended</span>
            </div>
            <h1 className="text-xl font-bold">Your account has been suspended</h1>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            {reason ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">Reason</p>
                <p className="text-sm text-slate-700 leading-relaxed">{reason}</p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Your account access has been restricted. Please contact support for more information.
              </p>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <HeadphonesIcon className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Support is still available.</span> You can raise a ticket or chat with our support team to resolve this.
              </p>
            </div>

            <button
              data-testid="suspended-go-to-support"
              onClick={() => navigate(supportPath)}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl px-6 py-3 transition-colors"
            >
              <HeadphonesIcon className="h-4 w-4" />
              Go to Support
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Footer hint */}
        <p className="text-center text-xs text-slate-400 mt-4">
          Once the issue is resolved and your account is reactivated, full access will be restored automatically.
        </p>
      </div>
    </div>
  );
};

// KYC gating: partners with non-approved KYC are sent to /partner/kyc-portal
const PARTNER_ROLES = ['reseller', 'sub_reseller'];

const ProtectedRoute = ({ children, allowedRoles, allowSuspended = false }) => {
  const { user, loading, isSuspended } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    // Role-specific login redirect based on attempted path
    const path = location.pathname;
    const loginPath = path.startsWith('/admin') ? '/admin/login'
      : path.startsWith('/client') ? '/client/login'
      : '/partner/login';
    return <Navigate to={loginPath} />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  // Suspension check comes BEFORE the KYC gate.
  // allowSuspended routes (e.g. /reseller/support) must be reachable
  // even when KYC is not approved — otherwise the "Go to Support" button
  // would be intercepted by the KYC redirect.
  if (isSuspended && !allowSuspended) {
    return <SuspendedScreen reason={user.suspension_reason} role={user.role} />;
  }

  // KYC gate: partners with non-approved KYC are sent to /partner/kyc-portal.
  // This does not run for suspended users on allowSuspended routes (support).
  if (!isSuspended && PARTNER_ROLES.includes(user.role) && user.kyc_status !== 'approved') {
    const kycExempt = ['/partner/kyc-portal', '/partner/edit-account', '/partner/edit-profile', '/profile'];
    if (!kycExempt.some(p => location.pathname.startsWith(p))) {
      return <Navigate to="/partner/kyc-portal" state={{ from: location }} />;
    }
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  const getDashboard = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'admin':
      case 'level_3_support':
      case 'level_2_support':
      case 'level_1_support':
        return '/admin/dashboard';
      case 'reseller':
      case 'sub_reseller':
        // KYC gate: redirect to portal if not approved
        return (user.kyc_status !== 'approved') ? '/partner/kyc-portal' : '/reseller/dashboard';
      case 'client':
        return '/client/dashboard';
      default:
        return '/login';
    }
  };

  return (
    <Routes>
      {/* Role-specific login pages */}
      <Route path="/admin/login" element={<Login />} />
      <Route path="/partner/login" element={<Login />} />
      <Route path="/client/login" element={<Login />} />
      {/* /login → Client login (clean URL for clients) */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* /partner/signup → Partner signup (clean URL for partners) */}
      <Route path="/partner/signup" element={<ResellerSignup />} />
      {/* /signup → Client signup */}
      <Route path="/signup" element={<ClientSignup />} />
      {/* Backward compat */}
      <Route path="/client/signup" element={<ClientSignup />} />
      <Route path="/auto-login" element={<AutoLogin />} />
      <Route path="/public/:subdomain" element={<PublicLanding />} />
      <Route path="/security" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />


      {/* Partner KYC Portal — accessible only when KYC is pending */}
      <Route
        path="/partner/kyc-portal"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <KYCPortal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/edit-account"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <AccountEditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/edit-profile"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ProfileEditPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/resellers"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminResellers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/revenue"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminRevenueTracking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/saas-orders"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <SaaSOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/wallets"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminWallets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transactions"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminTransactions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/activity-logs"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminActivityLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']} allowSuspended={true}>
            <AdminInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']} allowSuspended={true}>
            <AdminSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminUserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/quotations"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminQuotations />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/offers"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminOffers />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/documents"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support']}>
            <AdminDocuments />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/coupons"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support']}>
            <AdminCoupons />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/domains"
        element={
          <ProtectedRoute allowedRoles={['admin', 'level_3_support', 'level_2_support', 'level_1_support']}>
            <AdminDomainManager />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/whatsapp-settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminWhatsAppSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reseller/dashboard"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/pricing"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerPricing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/catalog"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/domains"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerDomains />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/clients"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/white-label"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerWhiteLabel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/quotations"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerQuotations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/orders"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/my-services"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <MyServices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/invoices"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']} allowSuspended={true}>
            <ResellerInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/wallet"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerWallet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/transactions"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <ResellerWalletTransactions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/support"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']} allowSuspended={true}>
            <ResellerSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/documents"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <PartnerDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseller/profile"
        element={
          <ProtectedRoute allowedRoles={['reseller', 'sub_reseller']}>
            <PartnerDashboardProfile />
          </ProtectedRoute>
        }
      />

      <Route
        path="/client/dashboard"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/client/services"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientServices />
          </ProtectedRoute>
        }
      />

      <Route
        path="/client/billing"
        element={
          <ProtectedRoute allowedRoles={['client']} allowSuspended={true}>
            <ClientBilling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/referral-program"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientReferralProgram />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/our-products"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientOurProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/buy-domain"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientBuyDomain />
          </ProtectedRoute>
        }
      />

      <Route
        path="/client/quotes-orders"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientQuotesAndOrders />
          </ProtectedRoute>
        }
      />

      <Route
        path="/client/support"
        element={
          <ProtectedRoute allowedRoles={['client']} allowSuspended={true}>
            <ClientSupport />
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={['admin', 'reseller', 'sub_reseller', 'client']}>
            <Profile />
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<Navigate to={getDashboard()} replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrandingProvider>
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
    </BrandingProvider>
  );
}

export default App;
